import { MaybeElement, until, useDebounceFn, useFullscreen, useIntersectionObserver } from '@vueuse/core';
import { nextTick, onMounted, Ref, watch } from 'vue';

import EventTools from '@/interactions/classes/EventTools';
import Page from '@/page/classes/Page';
import { usePage } from '@/page/composables/usePage';

export function useLazyCanvas(
	page: Ref<Page>,
	canvasIsVisible: Ref<boolean>,
	scrollArea: Ref<MaybeElement>,
	canvas: Ref<MaybeElement>,
	visibleTime: number
) {
	const { position } = usePage(page);
	const { isFullscreen } = useFullscreen();
	let watcher: ReturnType<typeof useIntersectionObserver> | null = null;

	const debouncedVisible = useDebounceFn((isIntersecting) => {
		canvasIsVisible.value = isIntersecting;
	}, visibleTime);

	const registerWatcher = async () => {
		if (watcher) {
			watcher.stop();
		}

		// Ya que es posible que el watcher se registre antes del cambio de posición
		// esperamos al siguiente tick para asegurarnos de que el canvas ya esta
		// en su sitio
		await nextTick();

		watcher = useIntersectionObserver(
			canvas,
			([{ isIntersecting }]) => {
				debouncedVisible(isIntersecting);
			},
			{
				threshold: 0,
				rootMargin: '15%',
				root: scrollArea,
			}
		);
	};

	onMounted(async () => {
		if (!scrollArea.value) {
			await until(() => scrollArea.value).not.toBeUndefined();
		}

		if (!scrollArea.value || !canvas.value) return;

		const rect = (canvas.value as HTMLElement).getBoundingClientRect();

		// lanzamos un primer check de visibilidad para no esperar a que el
		// interseccion observer se active. Con esto evitamos flashes iniciales
		canvasIsVisible.value =
			rect.top <= (scrollArea.value as HTMLElement).clientHeight &&
			rect.left <= (scrollArea.value as HTMLElement).clientWidth &&
			rect.bottom >= 0 &&
			rect.right >= 0;

		await registerWatcher();

		// Vigilamos la posición del elemento para registrar el observer si es que el canvas cambia de posición
		watch(position, registerWatcher);
		// Hay casos donde no se ejecuta el watcher de posición,
		// por el tiempo que transcurre entre que se registra el watcher y el ponerse en pantalla completa
		// asi que lo vigilamos y forzamos el registro del watcher de nuevo
		watch(isFullscreen, async () => {
			await EventTools.sleep(1000);
			registerWatcher();
		});
	});
}
